/**
 * The one query client both modules share, and how the panel reads through it.
 *
 * One client, because Agent code outside React — its stores, its chat stream —
 * reaches for the singleton by import, and a second client would be a second
 * cache those writes never touch. The defaults below came with that code and
 * stay its: they are wrong for a panel read in two ways, retrying a 403 as
 * readily as a 502 and refusing to retry on mount, so a screen that failed once
 * keeps showing the failure every time somebody comes back to it. `read`
 * restates the panel's rule.
 *
 * Keys are the thing to get right here. Everything about one brand lives
 * under `['entity', id]` and everything about one account under
 * `['org', id]`, so a write invalidates its whole scope rather than the one
 * list the screen it was made on happened to show — another screen's copy of
 * the same rows is still in the cache, and would otherwise come back stale.
 */

import { isServer, keepPreviousData, QueryClient, type QueryKey } from "@tanstack/react-query";
import { ApiError } from "~/core/api/index";

let browserQueryClient: QueryClient | undefined;

/**
 * The client, made once in a browser and per call anywhere else.
 *
 * `isServer` is not about a server this product has — there is none — but
 * about the Agent's vitest project, which runs in node: a singleton that
 * outlived a test file there would hand the next one somebody else's cache.
 */
export function getQueryClient(): QueryClient {
	if (isServer) return makeQueryClient();
	browserQueryClient ??= makeQueryClient();
	return browserQueryClient;
}

function makeQueryClient(): QueryClient {
	return new QueryClient({
		defaultOptions: {
			queries: {
				staleTime: 30 * 1000,
				// Tab-switch focus events are frequent and noisy. Per-query overrides
				// always win over this default.
				refetchOnWindowFocus: false,
				retry: isServer ? 0 : 1,
				retryOnMount: false,
			},
			mutations: { retry: false },
		},
	});
}

/**
 * Whether a failed request is worth asking again.
 *
 * A refusal the server meant — a 4xx with its own code — will be refused the
 * same way on the next attempt. A dropped connection (status 0, or no
 * `ApiError` at all because `fetch` itself threw) and a 5xx may not.
 */
export function transient(error: unknown): boolean {
	return !(error instanceof ApiError) || error.status === 0 || error.status >= 500;
}

/** Up to three more attempts, and only for a transient failure. */
export function retryRead(failures: number, error: unknown): boolean {
	return failures < 3 && transient(error);
}

/** One second, then two, then three. `failures` is zero before the first retry. */
export function retryDelay(failures: number): number {
	return (failures + 1) * 1000;
}

/**
 * The options every panel read takes.
 *
 * `staleTime: 0` because a screen mounted is a screen that asks again, as it
 * did before there was a cache: the worker changes these rows for minutes at a
 * time and not all of it is announced. What the cache adds is that the last
 * answer is on screen while the new one is on its way, and that two
 * components asking the same question in the same render ask it once.
 *
 * `keepPreviousData` because a filter change keeps the table in place until
 * the new one arrives. A skeleton on every change of period is a screen that
 * flashes empty on the control people use most.
 */
export const read = {
	retry: retryRead,
	retryDelay,
	retryOnMount: true,
	staleTime: 0,
	placeholderData: keepPreviousData,
} as const;

/**
 * A read that spends money: a model call made on mount.
 *
 * Never retried — a failure that reaches the client may already have been
 * paid for. Never refetched while it is on screen, not on focus and not on a
 * reconnect. And dropped from the cache as soon as nothing shows it, so the
 * next visit asks again rather than being handed an answer somebody else's
 * visit paid for under a key that no longer means the same thing.
 *
 * Keep these out of the `entity` and `org` scopes: a write elsewhere
 * invalidating its brand must never be what spends a model call.
 */
export const paid = {
	retry: 0,
	staleTime: Number.POSITIVE_INFINITY,
	gcTime: 0,
	refetchOnWindowFocus: false,
	refetchOnReconnect: false,
} as const;

/**
 * The error a query failed with, as the panel's own type.
 *
 * `fetch` throws a `TypeError` when the network is gone, and a query function
 * can throw anything; a screen wants a sentence it can translate.
 */
export function apiError(error: unknown): ApiError | null {
	if (error === null || error === undefined) return null;
	return error instanceof ApiError ? error : new ApiError(0, "internal", "errors.internal");
}

export const keys = {
	/** The person signed in. */
	me: ["me"] as const,
	/** The brands of one account: the shell's list, and the sidebar's. */
	brands: (org: string) => ["brands", org] as const,
	/** Everything about one account: its money, its people, its tasks. */
	org: (org: string) => ["org", org] as const,
	/** Everything about one brand. */
	entity: (entity: string) => ["entity", entity] as const,
};

/**
 * The account's event stream, by the names the server gives its events.
 *
 * Each one is a hint that something was written, not the record of what:
 * the stream can skip a row (`server/src/events`), and the answer to that
 * is to read again rather than to apply the event.
 */
export const ORG_EVENTS = [
	"billing.renewed",
	"credits.granted",
	"credits.spent",
	"entity.updated",
	"notification.created",
	"operation.updated",
	"page.generated",
	"page.published",
	"reflow.ready",
] as const;

/**
 * What one of those events makes stale.
 *
 * An event that names its brand invalidates that brand. A page's events name
 * the page and not the brand, so they invalidate every brand, which in
 * practice is the one on screen: an invalidation refetches only what is
 * mounted. The balance lives on the person's row and on the account's, and
 * the assistant is metered, so it moves while somebody is talking to it
 * rather than only on the next load.
 */
export function scopesFor(kind: string, data: string, org: string): QueryKey[] {
	const brand = brandOf(data);
	const scope: QueryKey = brand ? keys.entity(brand) : ["entity"];
	switch (kind) {
		case "billing.renewed":
		case "credits.granted":
		case "credits.spent":
			return [keys.me, keys.org(org)];
		// The brand list too, which the shell read again on these before there
		// was a cache.
		case "entity.updated":
		case "page.generated":
		case "page.published":
			return [keys.brands(org), scope];
		case "operation.updated":
		case "reflow.ready":
			return [scope];
		case "notification.created":
			return [[...keys.org(org), "notifications"]];
		default:
			return [];
	}
}

function brandOf(data: string): string | null {
	try {
		const payload: unknown = JSON.parse(data);
		const id = payload !== null && typeof payload === "object" ? (payload as { entity_id?: unknown }).entity_id : null;
		return typeof id === "string" && id !== "" ? id : null;
	} catch {
		return null;
	}
}
